/**
 * OverrideWarningModal - triggered when the server's preflight
 * returned BLOCK-severity violations.
 *
 * The agent reviews the server rule messages, writes a justification
 * (min 140 chars), acknowledges the audit, and confirms the override.
 * Submit fires with ``override_accepted=true`` and the lifecycle
 * service creates one ``RuleException`` per BLOCK for an approver to
 * resolve.
 *
 * Per PRD \u00a76 (Transfer Eligibility Rules) and \u00a713 (Exception Handling
 * dependency). No client-side day-count thresholds - the rules live
 * on the server.
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { AlertTriangle } from 'lucide-react'
import type { EligibilityState } from './eligibility'
import type { EligibilityViolation } from '@/admin/hooks/use-transfers'

const MIN_REASON_LENGTH = 140

interface OverrideWarningModalProps {
  open: boolean
  state: EligibilityState
  daysSinceClosing: number | null
  /**
   * The BLOCK-severity violations the server returned. Rendered in the
   * modal so the agent sees the exact rule messages that fired.
   */
  blockingViolations?: EligibilityViolation[]
  onCancel: () => void
  onConfirmOverride: (reason: string) => void
}

export function OverrideWarningModal({
  open,
  state,
  daysSinceClosing,
  blockingViolations = [],
  onCancel,
  onConfirmOverride,
}: OverrideWarningModalProps) {
  const [reason, setReason] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)

  // ``state !== 'override'`` is a safeguard - the wizard shouldn't open
  // this modal in other states, but we short-circuit defensively.
  const canConfirm =
    state === 'override' &&
    reason.trim().length >= MIN_REASON_LENGTH &&
    acknowledged

  const handleConfirm = () => {
    if (!canConfirm) return
    onConfirmOverride(reason.trim())
    setReason('')
    setAcknowledged(false)
  }

  const handleCancel = () => {
    setReason('')
    setAcknowledged(false)
    onCancel()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleCancel()
      }}
    >
      <DialogContent showCloseButton={false} className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[var(--status-warn)]" />
            <DialogTitle>Override Required</DialogTitle>
          </div>
          <DialogDescription>
            {daysSinceClosing !== null && daysSinceClosing > 0
              ? `This transfer is ${daysSinceClosing} day${daysSinceClosing === 1 ? '' : 's'} past closing and failed one or more of the tenant's eligibility rules. Confirm the override to proceed - an exception will be raised for approver review.`
              : 'This transfer failed one or more of the tenant\u2019s eligibility rules. Confirm the override to proceed - an exception will be raised for approver review.'}
          </DialogDescription>
        </DialogHeader>

        {blockingViolations.length > 0 && (
          <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3">
            <p className="clara-section-label mb-2">Rules that fired</p>
            <ul className="flex flex-col gap-1.5">
              {blockingViolations.map((v) => (
                <li
                  key={v.rule_uuid}
                  className="text-[12px] leading-relaxed text-[var(--ink-2)]"
                >
                  <span className="sr-only">Blocking rule: </span>
                  <span className="font-medium text-[var(--status-error)]">
                    {v.name}
                  </span>
                  {' - '}
                  {v.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-4 py-2">
          <div>
            <Label className="clara-section-label mb-1.5 block">
              Reason for override
            </Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Describe why this transfer is being approved despite the blocking rule (minimum 140 characters)."
              className="w-full resize-y rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--ink-3)] focus:outline-none"
              data-testid="override-reason"
            />
            <p className="mt-1 text-[11px] text-[var(--ink-3)]">
              {reason.trim().length} / {MIN_REASON_LENGTH} minimum characters
            </p>
          </div>

          <label className="flex cursor-pointer items-start gap-2">
            <Checkbox
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(!!v)}
              data-testid="override-acknowledge"
            />
            <span className="text-[12px] leading-relaxed text-[var(--ink-2)]">
              I understand this override is audited and creates a pending
              exception that must be resolved by an approver before the
              transfer completes.
            </span>
          </label>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="confirm-override-button"
          >
            Confirm Override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
